import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, relTime } from '../lib/api'
import { FromAddressPicker, useIsOperator } from '../components/FromAddress'
import { cm, cmBadge, type CmList } from '../lib/cm'

const PAGE = 50
const STATUSES = ['', 'subscribed', 'pending', 'unsubscribed', 'bounced']

/**
 * Subscriber lists. Consent is recorded for every member — where it came from, when, and from
 * which IP for public signups. Unsubscribing is per person, not per list: one click stops every
 * list at once.
 */
export function Lists() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['cm', 'lists'], queryFn: cm.lists })
  const operator = useIsOperator()
  const { data: boxes } = useQuery({ queryKey: ['cm', 'mailboxes'], queryFn: cm.mailboxes, enabled: operator })
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [mailbox, setMailbox] = useState('')
  const [fromEmail, setFromEmail] = useState('')
  const [doubleOptIn, setDoubleOptIn] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () => cm.createList({ name, default_mailbox_id: mailbox || undefined, default_from_email: mailbox ? undefined : fromEmail || undefined, double_opt_in: doubleOptIn }),
    onSuccess: () => { setCreating(false); setName(''); setError(null); qc.invalidateQueries({ queryKey: ['cm', 'lists'] }) },
    onError: e => setError((e as ApiError).message),
  })

  const rows = data?.data ?? []

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Lists</h1>
          <p className="lede">Who may receive your campaigns, and the proof that they agreed to.</p>
        </div>
        <button onClick={() => setCreating(c => !c)}>New list</button>
      </div>

      {creating && (
        <div className="card" style={{ marginBottom: 20 }}>
          <form className="row" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }} onSubmit={e => { e.preventDefault(); create.mutate() }}>
            <div className="field" style={{ flex: '2 1 220px', marginBottom: 0 }}><label>Name</label>
              <input required value={name} onChange={e => setName(e.target.value)} placeholder="Newsletter — itsbingmybusiness.com" /></div>
            <div className="field" style={{ flex: '2 1 260px', marginBottom: 0 }}><label>Confirmation emails sent from</label>
              <FromAddressPicker value={fromEmail} onChange={setFromEmail} /></div>
            {operator && (
              <div className="field" style={{ flex: '2 1 220px', marginBottom: 0 }}><label>…or a connected mailbox (operator)</label>
                <select value={mailbox} onChange={e => setMailbox(e.target.value)}>
                  <option value="">— none —</option>
                  {(boxes?.data ?? []).map(b => <option key={b.id} value={b.id}>{b.email}</option>)}
                </select></div>
            )}
            <label className="row small" style={{ gap: 6 }}>
              <input type="checkbox" checked={doubleOptIn} onChange={e => setDoubleOptIn(e.target.checked)} />
              Double opt-in (recommended)
            </label>
            <button type="submit" disabled={create.isPending}>Create</button>
          </form>
          {error && <div className="alert error" style={{ marginTop: 12 }}>{error}</div>}
        </div>
      )}

      {isLoading && <div className="skeleton" style={{ height: 200 }} />}
      {!isLoading && rows.length === 0 && (
        <div className="empty"><div className="big">☰</div><p><strong>No lists yet</strong></p>
          <p>Create one list per newsletter or per site, then add people who agreed to hear from you.</p></div>
      )}

      {rows.length > 0 && (
        <div className="card flush">
          <table className="data">
            <thead><tr><th>List</th><th>Subscribed</th><th>Awaiting confirmation</th><th>Unsubscribed</th><th>Bounced</th><th>Sender</th></tr></thead>
            <tbody>
              {rows.map(l => (
                <tr key={l.id}>
                  <td><Link to={`/lists/${l.id}`}><strong>{l.name}</strong></Link>
                    <div className="small muted">{l.double_opt_in ? 'Double opt-in' : 'Single opt-in'}</div></td>
                  <td>{l.subscribed.toLocaleString()}</td>
                  <td>{l.pending.toLocaleString()}</td>
                  <td>{l.unsubscribed.toLocaleString()}</td>
                  <td>{l.bounced.toLocaleString()}</td>
                  <td className="small mono">{l.default_mailbox ?? l.default_from_email ?? <span className="badge pending">not set</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export function ListDetail() {
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const { data: lists } = useQuery({ queryKey: ['cm', 'lists'], queryFn: cm.lists })
  const list = lists?.data.find(l => l.id === id)
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [panel, setPanel] = useState<'none' | 'add' | 'import' | 'form' | 'settings'>('none')

  const { data, isLoading } = useQuery({
    queryKey: ['cm', 'members', id, status, q, page],
    queryFn: () => cm.members(id, { status: status || undefined, q: q || undefined, limit: PAGE, offset: page * PAGE }),
  })
  const unsub = useMutation({
    mutationFn: (contactId: string) => cm.unsubscribeContact(contactId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cm'] }),
  })
  const refresh = () => qc.invalidateQueries({ queryKey: ['cm'] })

  if (!list) return lists ? <div className="alert error">List not found.</div> : <div className="skeleton" style={{ height: 300 }} />

  const rows = data?.data ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <p><Link to="/lists">← Lists</Link></p>
      <div className="page-head">
        <div>
          <h1>{list.name}</h1>
          <p className="lede">{list.subscribed} subscribed · {list.pending} awaiting confirmation · {list.unsubscribed} unsubscribed · {list.bounced} bounced</p>
        </div>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <button className="secondary" onClick={() => setPanel(panel === 'add' ? 'none' : 'add')}>Add person</button>
          <button className="secondary" onClick={() => setPanel(panel === 'import' ? 'none' : 'import')}>Import CSV</button>
          <button className="secondary" onClick={() => setPanel(panel === 'form' ? 'none' : 'form')}>Signup form</button>
          <button className="secondary" onClick={() => setPanel(panel === 'settings' ? 'none' : 'settings')}>Settings</button>
        </div>
      </div>

      {!list.default_mailbox && !list.default_from_email && (
        <div className="alert" style={{ marginBottom: 16 }}>
          No sender is set for this list, so confirmation emails cannot go out. Choose one under Settings.
        </div>
      )}

      {panel === 'add' && <AddMember list={list} onDone={() => { setPanel('none'); refresh() }} />}
      {panel === 'import' && <ImportMembers list={list} onDone={refresh} />}
      {panel === 'form' && <SignupForm list={list} />}
      {panel === 'settings' && <ListSettings list={list} onDone={() => { setPanel('none'); refresh() }} />}

      <div className="toolbar">
        <div className="segmented" role="tablist">
          {STATUSES.map(s => (
            <button key={s || 'all'} role="tab" aria-selected={status === s} className={status === s ? 'active' : ''}
                    onClick={() => { setStatus(s); setPage(0) }}>{s === '' ? 'All' : cmBadge(s).text}</button>
          ))}
        </div>
        <form className="row" style={{ flex: '1 1 220px' }} onSubmit={e => { e.preventDefault(); setQ(search.trim()); setPage(0) }}>
          <input placeholder="Search email or name…" value={search} onChange={e => setSearch(e.target.value)} />
          <button type="submit" className="secondary">Search</button>
        </form>
      </div>

      {isLoading && <div className="skeleton" style={{ height: 200 }} />}
      {!isLoading && rows.length === 0 && <div className="empty"><p><strong>Nobody here</strong></p></div>}
      {rows.length > 0 && (
        <>
          <div className="card flush">
            <table className="data">
              <thead><tr><th>Person</th><th>Status</th><th>Consent</th><th>Since</th><th /></tr></thead>
              <tbody>
                {rows.map(m => {
                  const b = cmBadge(m.status)
                  return (
                    <tr key={m.contact_id}>
                      <td><div className="mono small">{m.email}</div>{m.name && <div className="small muted">{m.name}</div>}</td>
                      <td><span className={`badge ${b.cls}`}>{b.text}</span>
                        {m.status_reason && <div className="small muted truncate" title={m.status_reason}>{m.status_reason}</div>}</td>
                      <td className="small muted truncate" title={`${m.consent_source}${m.consent_ip ? ` · IP ${m.consent_ip}` : ''}`}>
                        {m.consent_source}{m.consent_ip ? ` · ${m.consent_ip}` : ''}</td>
                      <td className="small muted" title={new Date(m.consent_at).toLocaleString()}>{relTime(m.confirmed_at ?? m.consent_at)}</td>
                      <td className="right">
                        {(m.status === 'subscribed' || m.status === 'pending') && (
                          <button className="ghost" onClick={() => {
                            if (confirm(`Unsubscribe ${m.email} from ALL lists?`)) unsub.mutate(m.contact_id)
                          }}>Unsubscribe</button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="pager">
            <button className="secondary" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Newer</button>
            <span className="small muted">{page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} of {total.toLocaleString()}</span>
            <button className="secondary" disabled={(page + 1) * PAGE >= total} onClick={() => setPage(p => p + 1)}>Older →</button>
          </div>
        </>
      )}
    </div>
  )
}

function AddMember({ list, onDone }: { list: CmList; onDone: () => void }) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [consent, setConsent] = useState('')
  const [confirmFirst, setConfirmFirst] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const add = useMutation({
    mutationFn: () => cm.addMember(list.id, { email, name: name || undefined, consent_source: consent, send_confirmation: confirmFirst }),
    onSuccess: onDone,
    onError: e => setError((e as ApiError).message),
  })
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <form onSubmit={e => { e.preventDefault(); add.mutate() }}>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <div className="field" style={{ flex: '1 1 220px' }}><label>Email</label><input type="email" required value={email} onChange={e => setEmail(e.target.value)} /></div>
          <div className="field" style={{ flex: '1 1 180px' }}><label>Name</label><input value={name} onChange={e => setName(e.target.value)} /></div>
          <div className="field" style={{ flex: '2 1 280px' }}><label>How did they agree?</label>
            <input required value={consent} onChange={e => setConsent(e.target.value)} placeholder="Ticked the newsletter box at checkout, 12 Oct" /></div>
        </div>
        <label className="row small" style={{ gap: 6, marginBottom: 12 }}>
          <input type="checkbox" checked={confirmFirst} onChange={e => setConfirmFirst(e.target.checked)} />
          Send them a confirmation email first (they are only subscribed once they click it)
        </label>
        {error && <div className="alert error">{error}</div>}
        <button type="submit" disabled={add.isPending}>Add</button>
      </form>
    </div>
  )
}

function ImportMembers({ list, onDone }: { list: CmList; onDone: () => void }) {
  const [csv, setCsv] = useState('')
  const [consent, setConsent] = useState('')
  const [confirmFirst, setConfirmFirst] = useState(true)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const run = useMutation({
    mutationFn: () => cm.importMembers(list.id, { csv, consent_source: consent, send_confirmation: confirmFirst }),
    onSuccess: r => {
      setError(null); setCsv('')
      setResult(`${r.added} added · ${r.already_on_list} already on the list · ${r.invalid} invalid or duplicate · ${r.unsubscribed_or_bounced} skipped (unsubscribed or bounced)`)
      onDone()
    },
    onError: e => setError((e as ApiError).message),
  })
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <p className="small muted" style={{ marginTop: 0 }}>
        One person per line: <span className="mono">email,name</span>. Only import people who agreed to receive
        email from you — never bought or scraped lists. People who unsubscribed or bounced are skipped automatically.
      </p>
      <form onSubmit={e => { e.preventDefault(); run.mutate() }}>
        <div className="field"><label>CSV</label>
          <textarea rows={8} required value={csv} onChange={e => setCsv(e.target.value)} placeholder={'jane@example.com,Jane Doe\nali@example.com,Ali Khan'} /></div>
        <div className="field"><label>Where did these people agree to receive email?</label>
          <input required value={consent} onChange={e => setConsent(e.target.value)} placeholder="Website newsletter signups, Jan–Oct 2026" /></div>
        <label className="row small" style={{ gap: 6, marginBottom: 12 }}>
          <input type="checkbox" checked={confirmFirst} onChange={e => setConfirmFirst(e.target.checked)} />
          Ask each person to confirm by email first (safest — unconfirmed people never get campaigns)
        </label>
        {error && <div className="alert error">{error}</div>}
        {result && <div className="alert">{result}</div>}
        <button type="submit" disabled={run.isPending}>{run.isPending ? 'Importing…' : 'Import'}</button>
      </form>
    </div>
  )
}

function SignupForm({ list }: { list: CmList }) {
  const snippet = `<form action="${list.subscribe_url}" method="post">
  <input type="email" name="email" placeholder="Your email" required>
  <input type="text" name="name" placeholder="Your name">
  <input type="text" name="website" style="display:none" tabindex="-1" autocomplete="off">
  <button type="submit">Subscribe</button>
</form>`
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <p className="small muted" style={{ marginTop: 0 }}>
        Paste this into any page on your site. {list.double_opt_in
          ? 'New signups get a confirmation email and are only subscribed after they click it.'
          : 'Signups are subscribed straight away.'} The hidden <span className="mono">website</span> field catches bots.
      </p>
      <pre className="mono small" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{snippet}</pre>
      <button className="secondary" style={{ marginTop: 12 }} onClick={() => navigator.clipboard?.writeText(snippet)}>Copy</button>
    </div>
  )
}

function ListSettings({ list, onDone }: { list: CmList; onDone: () => void }) {
  const operator = useIsOperator()
  const { data: boxes } = useQuery({ queryKey: ['cm', 'mailboxes'], queryFn: cm.mailboxes, enabled: operator })
  const [name, setName] = useState(list.name)
  const [mailbox, setMailbox] = useState(list.default_mailbox_id ?? '')
  const [fromEmail, setFromEmail] = useState(list.default_from_email ?? '')
  const [doubleOptIn, setDoubleOptIn] = useState(list.double_opt_in)
  const [redirect, setRedirect] = useState(list.redirect_url ?? '')
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () => cm.updateList(list.id, {
      name, double_opt_in: doubleOptIn, redirect_url: redirect,
      ...(mailbox ? { default_mailbox_id: mailbox } : { default_from_email: fromEmail }),
    }),
    onSuccess: onDone,
    onError: e => setError((e as ApiError).message),
  })
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <form onSubmit={e => { e.preventDefault(); save.mutate() }}>
        <div className="field"><label>Name</label><input value={name} onChange={e => setName(e.target.value)} /></div>
        <div className="field"><label>Confirmation emails sent from</label>
          <FromAddressPicker value={fromEmail} onChange={setFromEmail} /></div>
        {operator && (
          <div className="field"><label>…or a connected mailbox (operator)</label>
            <select value={mailbox} onChange={e => setMailbox(e.target.value)}>
              <option value="">— none —</option>
              {(boxes?.data ?? []).map(b => <option key={b.id} value={b.id}>{b.email}</option>)}
            </select></div>
        )}
        <div className="field"><label>After signing up, send people to (optional)</label>
          <input value={redirect} onChange={e => setRedirect(e.target.value)} placeholder="https://example.com/thanks" /></div>
        <label className="row small" style={{ gap: 6, marginBottom: 12 }}>
          <input type="checkbox" checked={doubleOptIn} onChange={e => setDoubleOptIn(e.target.checked)} /> Double opt-in
        </label>
        {error && <div className="alert error">{error}</div>}
        <button type="submit" disabled={save.isPending}>Save</button>
      </form>
    </div>
  )
}
